import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type TextareaHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import {
  IconAlert,
  IconCheck,
  IconChevronRight,
  IconInbox,
  IconRefresh,
  IconX,
} from '@/components/icons';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Base classes for a form control, with the caller's own width winning.
 *
 * `cx` is a plain joiner, not tailwind-merge, so `cx('w-full', 'w-auto')`
 * emits both and the browser picks by stylesheet order rather than by the
 * order they were written. `w-full` wins, and every `className="w-auto"` in
 * this app silently did nothing -- which is why a four-option window selector
 * spanned the page on fourteen screens.
 *
 * Rather than adding a dependency to resolve every Tailwind conflict, this
 * resolves the one that actually occurs: an explicit width replaces the
 * default one. `max-w-` and `min-w-` are constraints rather than replacements,
 * so they do not trigger it.
 */
function control(base: string, className?: string): string {
  const setsWidth = /(?:^|\s)w-\S/.test(className ?? '');
  // A lookbehind, not \b: a hyphen IS a word boundary, so \bw-full\b
  // happily matches inside "max-w-full" and leaves "max-" behind.
  return cx(setsWidth ? base.replace(/(?<![-\w])w-full\b/, '') : base, className);
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  // `bg-accent` rather than `bg-brand-600`: the primary button is the single
  // most brand-carrying element in the portal, and it has to be the exact
  // colour the operator chose on the Branding screen. `text-accent-fg` is
  // white or near-black, whichever reads on that colour -- white on a pale
  // yellow accent was unreadable.
  //
  // Every variant carries a 1px border, transparent where it is not wanted,
  // so a primary and a secondary button side by side are the same height and
  // their labels sit on the same baseline.
  primary:
    'border-transparent bg-accent text-accent-fg shadow-sm ' +
    'hover:bg-accent/90 active:bg-accent/80',
  secondary:
    'border-line-strong bg-surface text-ink-800 shadow-sm ' +
    'hover:bg-sunken active:bg-ink-100 ' +
    'dark:text-ink-100 dark:hover:bg-raised dark:active:bg-ink-800',
  ghost:
    'border-transparent text-ink-600 hover:bg-ink-100 hover:text-ink-900 active:bg-ink-200/70 ' +
    'dark:text-ink-300 dark:hover:bg-white/[0.06] dark:hover:text-ink-50 dark:active:bg-white/10',
  danger:
    'border-transparent bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-red-800 ' +
    'dark:hover:bg-red-500',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-7 gap-1 px-2.5 text-xs',
  md: 'h-8 gap-1.5 px-3 text-sm',
  lg: 'h-10 gap-2 px-4 text-sm',
  // Square, for a lone glyph. Give it an aria-label at the call site.
  icon: 'h-8 w-8 p-0 text-base',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /**
   * A glyph before the label.
   *
   * Replaced by the spinner while loading rather than shown beside it: two
   * marks where there was one makes the button change width mid-click, and
   * the spinner is already saying what the icon would.
   */
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    icon,
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // No default `type`: some forms rely on a bare <Button> submitting
      // them, and a restyle must not change what a click does.
      disabled={disabled || loading}
      className={cx(
        'inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-md border font-medium',
        'transition-[background-color,border-color,color,box-shadow] duration-100',
        'focus-visible:ring-2 focus-visible:ring-brand-500/50 focus-visible:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner className="h-3.5 w-3.5" />
      ) : (
        icon && <span className="-ml-0.5 shrink-0 text-[1.05em] opacity-90">{icon}</span>
      )}
      {children}
    </button>
  );
});

/**
 * A square icon button for a row of actions -- edit, suspend, delete.
 *
 * The label is the tooltip and the accessible name, so an icon never has to
 * be guessed at. `danger` turns red on hover rather than at rest: a column of
 * red bins down a table shouts about the one thing nobody should press by
 * accident.
 */
export function IconAction({
  label,
  onClick,
  children,
  tone = 'default',
  busy,
  disabled,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  tone?: 'default' | 'danger';
  busy?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled || busy}
      className={cx(
        'inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-[15px] transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
        tone === 'danger'
          ? 'text-ink-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600 dark:text-ink-400 dark:hover:border-red-400/30 dark:hover:bg-red-400/10 dark:hover:text-red-400'
          : 'text-ink-500 hover:border-line hover:bg-sunken hover:text-ink-900 dark:text-ink-400 dark:hover:text-ink-50',
      )}
    >
      {busy ? <Spinner className="h-3.5 w-3.5" /> : children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx('animate-spin', className ?? 'h-4 w-4')}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Form controls
// ---------------------------------------------------------------------------

// One definition for every text control. `bg-surface` and `border-line-strong`
// come from the CSS variables, so a control cannot look right in light mode
// and wrong in dark — which is what happened when each carried its own `dark:`.
const FIELD_BASE =
  'block w-full rounded-md border border-line-strong bg-surface px-2.5 text-sm text-ink-900 shadow-sm ' +
  'transition-[border-color,box-shadow] duration-100 ' +
  'placeholder:text-ink-400 ' +
  'hover:border-ink-400 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-[3px] focus:ring-brand-500/20 ' +
  'focus-visible:ring-offset-0 ' +
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-500 disabled:shadow-none ' +
  'dark:text-ink-50 dark:shadow-none dark:placeholder:text-ink-500 dark:hover:border-ink-600 ' +
  'dark:focus:border-brand-400';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={control(cx(FIELD_BASE, 'h-8'), className)} {...rest} />;
  },
);

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(function Select({ className, children, ...rest }, ref) {
  return (
    <select ref={ref} className={control(cx(FIELD_BASE, 'h-8 cursor-pointer pr-8'), className)} {...rest}>
      {children}
    </select>
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return (
    <textarea ref={ref} className={control(cx(FIELD_BASE, 'py-2 font-mono leading-relaxed'), className)} {...rest} />
  );
});

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-ink-700 dark:text-ink-200">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {children}
      {hint && !error && (
        <span className="mt-1.5 block text-xs text-ink-500 dark:text-ink-400">{hint}</span>
      )}
      {error && <span className="mt-1.5 block text-xs text-red-600 dark:text-red-400">{error}</span>}
    </label>
  );
}


/**
 * A labelled checkbox.
 *
 * Lived privately inside the bot settings page until three other screens
 * needed one. A second copy is how two switches on the same product end up
 * looking and behaving differently.
 */
export function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cx(
        'flex items-start gap-2.5 text-sm text-ink-700 dark:text-ink-200',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-line-strong accent-brand-600"
      />
      <span>
        {label && <span className="font-medium">{label}</span>}
        {hint && <span className="block text-xs text-ink-500 dark:text-ink-400">{hint}</span>}
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({
  title,
  description,
  actions,
  footer,
  children,
  className,
  bodyClassName,
  padded = true,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** A strip under the body for secondary actions or a summary line. */
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  padded?: boolean;
}) {
  return (
    <section
      className={cx(
        'rounded-lg border border-line bg-surface shadow-card',
        className,
      )}
    >
      {(title || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b border-line px-4 py-3">
          <div className="min-w-0">
            {title && (
              <h2 className="truncate text-[15px] font-semibold leading-6 text-ink-900 dark:text-ink-50">
                {title}
              </h2>
            )}
            {description && (
              <p className="text-xs text-ink-500 dark:text-ink-400">{description}</p>
            )}
          </div>
          {/* Allowed to shrink and wrap: a table toolbar of five filters is
              wider than a phone, and `shrink-0` here pushed it out of the
              card and scrolled the whole page sideways. */}
          {actions && <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={cx(padded && 'p-4', bodyClassName)}>{children}</div>
      {footer && (
        <footer className="flex flex-wrap items-center justify-end gap-2 rounded-b-lg border-t border-line bg-sunken/60 px-4 py-2.5 text-xs text-ink-500 dark:text-ink-400">
          {footer}
        </footer>
      )}
    </section>
  );
}


/**
 * A row of tabs that looks the same everywhere.
 *
 * Four screens had grown their own, each with its own idea of the underline
 * colour and the inactive text. A console where two tab strips do not match is
 * a console that looks unfinished however good either one is.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  counts,
}: {
  tabs: readonly T[];
  value: T;
  onChange: (next: T) => void;
  counts?: Partial<Record<T, number>>;
}) {
  return (
    <div role="tablist" className="flex gap-4 overflow-x-auto border-b border-line">
      {tabs.map((tab) => {
        const active = tab === value;
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab)}
            className={cx(
              'relative -mb-px inline-flex items-center whitespace-nowrap border-b-2 px-0.5 pb-2.5 pt-1 text-sm transition-colors',
              active
                ? 'border-brand-600 font-medium text-ink-900 dark:border-brand-400 dark:text-ink-50'
                : 'border-transparent text-ink-500 hover:border-line-strong hover:text-ink-800 dark:text-ink-400 dark:hover:text-ink-200',
            )}
          >
            {tab}
            {counts?.[tab] !== undefined && (
              <span
                className={cx(
                  'ml-1.5 rounded-full px-1.5 text-2xs font-medium tabular-nums',
                  active
                    ? 'bg-brand-600/10 text-brand-700 dark:bg-brand-400/15 dark:text-brand-300'
                    : 'bg-ink-100 text-ink-500 dark:bg-white/[0.06] dark:text-ink-400',
                )}
              >
                {counts[tab]}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A number against the same number earlier.
 *
 * "412 calls" is not information. "412, against 380 by this time yesterday" is
 * something somebody can act on, and it is the difference between a dashboard
 * being read and being glanced at.
 *
 * `goodIsUp` because half of these are the wrong way round: more calls is
 * good, more errors is not, and a green arrow on a rising error count is worse
 * than no arrow at all.
 */
export function Delta({
  now,
  before,
  goodIsUp = true,
  suffix = '',
}: {
  now: number;
  before: number | null | undefined;
  goodIsUp?: boolean;
  suffix?: string;
}) {
  if (before === null || before === undefined || before === 0) return null;
  const change = ((now - before) / before) * 100;
  if (!isFinite(change) || Math.abs(change) < 1) {
    return <span className="text-2xs text-ink-400">level{suffix}</span>;
  }
  const up = change > 0;
  const good = up === goodIsUp;
  return (
    <span
      className={cx(
        'inline-flex items-center rounded px-1 text-2xs font-medium tabular-nums',
        good
          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-400'
          : 'bg-red-50 text-red-700 dark:bg-red-400/10 dark:text-red-400',
      )}
    >
      {up ? '↑' : '↓'} {Math.abs(change).toFixed(0)}%{suffix}
    </span>
  );
}

/**
 * Asking before something cannot be undone.
 *
 * A `window.confirm` is the alternative, and it is wrong for two reasons: it
 * cannot say what is about to be destroyed in any detail, and it looks like a
 * browser rather than like this product. Destructive confirmation is exactly
 * where somebody should be reading carefully.
 *
 * `requireTyping` is for the ones where a mis-click would genuinely hurt: the
 * button stays dead until the operator types the word. Reserved for actions
 * whose blast radius is the whole platform.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Delete',
  requireTyping,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  requireTyping?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const armed = !requireTyping || typed.trim().toUpperCase() === requireTyping.toUpperCase();

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" disabled={!armed || busy} onClick={onConfirm}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm text-ink-700 dark:text-ink-200">
        {body}
        {requireTyping && (
          <Field label={`Type ${requireTyping} to confirm`}>
            <Input
              value={typed}
              autoFocus
              onChange={(e) => setTyped(e.target.value)}
              placeholder={requireTyping}
            />
          </Field>
        )}
      </div>
    </Modal>
  );
}

/**
 * A level, a state, a status — coloured consistently wherever it appears.
 *
 * Log levels in particular had been coloured ad hoc on every screen that
 * showed one, so ERROR was amber in one place and red in another.
 */
const LEVEL_TONES: Record<string, BadgeTone> = {
  CRITICAL: 'red',
  ERROR: 'red',
  WARNING: 'amber',
  WARN: 'amber',
  INFO: 'blue',
  DEBUG: 'gray',
};

export function LevelBadge({ level }: { level: string }) {
  const key = (level || '').toUpperCase();
  return <Badge tone={LEVEL_TONES[key] ?? 'gray'}>{key || 'LOG'}</Badge>;
}

export interface Crumb {
  label: ReactNode;
  /** Omit for the current page. */
  to?: string;
}

/** `Section / Page / Detail`, with every step but the last a link. */
export function Breadcrumbs({ items, className }: { items: Crumb[]; className?: string }) {
  if (items.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className={cx('min-w-0', className)}>
      <ol className="flex min-w-0 items-center gap-1 text-xs text-ink-500 dark:text-ink-400">
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <li key={index} className={cx('flex min-w-0 items-center gap-1', last && 'min-w-0')}>
              {index > 0 && <IconChevronRight className="shrink-0 text-[0.9em] opacity-60" />}
              {item.to && !last ? (
                <Link
                  to={item.to}
                  className="truncate rounded-sm transition-colors hover:text-ink-900 dark:hover:text-ink-100"
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  aria-current={last ? 'page' : undefined}
                  className={cx('truncate', last && 'font-medium text-ink-800 dark:text-ink-100')}
                >
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Optional trail above the title, e.g. `[{label:'Bots', to:'/bots'}, {label: bot.name}]`. */
  breadcrumb?: Crumb[];
}) {
  // The rule underneath is not decoration. Without it the page title sits in
  // the same undifferentiated column as the first card's own heading, and the
  // two compete -- which is most of why a dense page reads as a wall.
  return (
    <div className="mb-6 border-b border-line pb-5">
      {breadcrumb && breadcrumb.length > 0 && <Breadcrumbs items={breadcrumb} className="mb-2" />}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-ink-900 dark:text-ink-50">
            {title}
          </h1>
          {description && (
            <p className="mt-1 max-w-3xl text-sm text-ink-500 dark:text-ink-400">
              {description}
            </p>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

/**
 * One number.
 *
 * Both sizes here came down. A number set at 30px on a card holding nothing
 * else reads as a poster rather than as data, and six of them across the top
 * of a dashboard leave no room for the thing they are supposed to introduce --
 * which on this platform is always the more useful half of the page.
 *
 * The standard size is a heading rather than a headline, and `dense` is for
 * rows of six or more, where the job is to be scanned rather than read.
 *
 * `delta` sits beside the number (pass a <Delta/>); `hint` and `sub` are the
 * same line underneath -- `sub` is the original name and still works.
 */
const STAT_ACCENTS = {
  blue: 'bg-blue-50 text-blue-600 ring-blue-600/10 dark:bg-blue-400/10 dark:text-blue-300 dark:ring-blue-400/20',
  green: 'bg-emerald-50 text-emerald-600 ring-emerald-600/10 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/20',
  amber: 'bg-amber-50 text-amber-600 ring-amber-600/10 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/20',
  violet: 'bg-violet-50 text-violet-600 ring-violet-600/10 dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/20',
  red: 'bg-red-50 text-red-600 ring-red-600/10 dark:bg-red-400/10 dark:text-red-300 dark:ring-red-400/20',
  sky: 'bg-sky-50 text-sky-600 ring-sky-600/10 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/20',
  slate: 'bg-ink-100 text-ink-600 ring-ink-500/10 dark:bg-white/[0.06] dark:text-ink-300 dark:ring-white/10',
} as const;

export function StatCard({
  label,
  value,
  sub,
  hint,
  delta,
  icon,
  accent,
  tone = 'default',
  dense = false,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  hint?: ReactNode;
  delta?: ReactNode;
  icon?: ReactNode;
  /** Colours the icon chip. Without it the icon sits quietly in grey. */
  accent?: 'blue' | 'green' | 'amber' | 'violet' | 'red' | 'sky' | 'slate';
  tone?: 'default' | 'good' | 'warn' | 'bad';
  dense?: boolean;
}) {
  const tones = {
    default: 'text-ink-900 dark:text-ink-50',
    good: 'text-emerald-600 dark:text-emerald-400',
    warn: 'text-amber-600 dark:text-amber-400',
    bad: 'text-red-600 dark:text-red-400',
  };
  const footnote = hint ?? sub;
  return (
    <div
      className={cx(
        'rounded-lg border border-line bg-surface shadow-card',
        dense ? 'px-3 py-2.5' : 'px-4 py-3.5',
      )}
    >
      {/*
        Wraps to a second line rather than truncating. Six of these across a
        row turned "Answering machines" into "ANSWERING MACHI…" and "Human
        conversations" into "HUMAN CONVERSA…", which is a label that has
        failed at the only thing a label does. The reserved second line keeps
        the numbers on one baseline across the row whether or not it is used.
      */}
      <div className="flex items-start justify-between gap-2">
        <p className="line-clamp-2 min-h-[2.5em] text-xs font-semibold leading-[1.25em] text-ink-600 dark:text-ink-300">
          {label}
        </p>
        {icon &&
          (accent ? (
            <span
              className={cx(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[17px] ring-1 ring-inset',
                STAT_ACCENTS[accent],
              )}
            >
              {icon}
            </span>
          ) : (
            <span className="shrink-0 text-base text-ink-400 dark:text-ink-500">{icon}</span>
          ))}
      </div>
      <div className={cx('flex flex-wrap items-baseline gap-x-2', dense ? 'mt-0.5' : 'mt-1')}>
        <p
          className={cx(
            'font-bold tabular-nums leading-tight tracking-tight',
            dense ? 'text-lg' : 'text-2xl',
            tones[tone],
          )}
        >
          {value}
        </p>
        {delta}
      </div>
      {footnote && (
        <p className="mt-1 truncate text-2xs text-ink-500 dark:text-ink-400">{footnote}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

type BadgeTone = 'gray' | 'blue' | 'green' | 'amber' | 'red' | 'purple';

// Status colours are fixed, deliberately NOT the brand ramp: an operator who
// brands the portal green must not make every "connected" badge look like a
// "qualified" one.
const BADGE_TONES: Record<BadgeTone, string> = {
  gray: 'bg-ink-100 text-ink-700 ring-ink-500/15 dark:bg-white/[0.06] dark:text-ink-300 dark:ring-white/10',
  blue: 'bg-blue-50 text-blue-700 ring-blue-600/15 dark:bg-blue-400/10 dark:text-blue-300 dark:ring-blue-400/20',
  green:
    'bg-emerald-50 text-emerald-700 ring-emerald-600/15 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/20',
  amber:
    'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/20',
  red: 'bg-red-50 text-red-700 ring-red-600/15 dark:bg-red-400/10 dark:text-red-300 dark:ring-red-400/20',
  purple:
    'bg-violet-50 text-violet-700 ring-violet-600/15 dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/20',
};

const BADGE_DOTS: Record<BadgeTone, string> = {
  gray: 'bg-ink-400',
  blue: 'bg-blue-500',
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  purple: 'bg-violet-500',
};

export function Badge({
  children,
  tone = 'gray',
  className,
  title,
  dot,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
  /** Hover text. A badge often IS the summary of something longer — a
      provider's FAILED carries the vendor's own message behind it. */
  title?: string;
  /** A small status dot before the label. */
  dot?: boolean;
}) {
  return (
    <span
      title={title}
      className={cx(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-px text-xs font-medium ring-1 ring-inset',
        BADGE_TONES[tone],
        className,
      )}
    >
      {dot && <span className={cx('h-1.5 w-1.5 shrink-0 rounded-full', BADGE_DOTS[tone])} />}
      {children}
    </span>
  );
}

/** Colour a call state so an operator can scan the board without reading. */
const CALL_STATE_TONES: Record<string, BadgeTone> = {
  CALLING: 'gray',
  RINGING: 'gray',
  CONNECTED: 'blue',
  AI_ACTIVE: 'blue',
  QUALIFYING: 'blue',
  QUALIFIED: 'green',
  TRANSFER_REQUESTED: 'amber',
  TRANSFER_RINGING: 'amber',
  HUMAN_CONNECTED: 'green',
  NOT_INTERESTED: 'gray',
  DNC: 'red',
  CALLBACK: 'purple',
  NO_ANSWER: 'gray',
  BUSY: 'gray',
  FAILED: 'red',
  ENDED: 'gray',
};

/**
 * A call's state, named for the engine that is in it.
 *
 * `AI_ACTIVE` is the state a Soundboard Bot sits in too — it shares the call
 * state machine with the AI engine deliberately, because everything around a
 * call is the same and a second set of states is the copy that drifts. But
 * "AI ACTIVE" on a board that has no model and no voice is a lie on the one
 * screen somebody opens to understand a call, and it was asked about
 * directly.
 *
 * So the state is unchanged and the WORD is not. `kind` is optional: a caller
 * that does not know which engine it is looking at gets exactly what it got
 * before.
 */
export function CallStateBadge({
  state,
  kind,
}: {
  state: string;
  kind?: string | null;
}) {
  const label =
    kind === 'SOUNDBOARD' && state === 'AI_ACTIVE'
      ? 'PLAYING'
      : state.replace(/_/g, ' ');
  return (
    <Badge tone={CALL_STATE_TONES[state] ?? 'gray'} dot>
      {label}
    </Badge>
  );
}

const INTENT_TONES: Record<string, BadgeTone> = {
  QUALIFIED: 'green',
  NOT_INTERESTED: 'gray',
  DO_NOT_CALL: 'red',
  WRONG_NUMBER: 'amber',
  CALLBACK: 'purple',
  NEUTRAL: 'gray',
};

export function IntentBadge({ intent }: { intent: string | null | undefined }) {
  if (!intent) return <span className="text-ink-400">—</span>;
  return <Badge tone={INTENT_TONES[intent] ?? 'gray'}>{intent.replace(/_/g, ' ')}</Badge>;
}

export function ScoreBadge({ score }: { score: number | null | undefined }) {
  if (score === null || score === undefined) return <span className="text-ink-400">—</span>;
  const tone: BadgeTone = score >= 80 ? 'green' : score >= 60 ? 'blue' : score >= 40 ? 'amber' : 'gray';
  return <Badge tone={tone}>{score}</Badge>;
}

export function LiveDot({ label = 'Live' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-pulse-dot rounded-full bg-emerald-400/60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------
//
// Thin wrappers over the `.glee-table` classes in styles.css. A hand-written
// `<table className="glee-table">` gets exactly the same look; these exist so
// the numeric-column and clickable-row conventions are props rather than
// class names somebody has to remember.

/**
 * The scroll container. Wide tables scroll sideways inside it; pass
 * `maxHeight` to make the header sticky while rows scroll under it.
 */
export function TableWrap({
  children,
  maxHeight,
  className,
}: {
  children: ReactNode;
  maxHeight?: number | string;
  className?: string;
}) {
  return (
    <div className={cx('glee-table-wrap', className)} style={maxHeight ? { maxHeight } : undefined}>
      {children}
    </div>
  );
}

export function Table({
  compact,
  className,
  children,
  ...rest
}: TableHTMLAttributes<HTMLTableElement> & {
  /** Tighter rows and no outer gutter -- for a small table inside a padded card. */
  compact?: boolean;
}) {
  return (
    <table className={cx('glee-table', compact && 'glee-table--compact', className)} {...rest}>
      {children}
    </table>
  );
}

export function THead(props: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead {...props} />;
}

export function TBody(props: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props} />;
}

export function TFoot(props: HTMLAttributes<HTMLTableSectionElement>) {
  return <tfoot {...props} />;
}

export function TR({ className, onClick, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr onClick={onClick} className={cx(onClick && 'is-clickable', className)} {...rest} />;
}

export function TH({
  numeric,
  className,
  scope = 'col',
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return <th scope={scope} className={cx(numeric && 'num', className)} {...rest} />;
}

export function TD({
  numeric,
  className,
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return <td className={cx(numeric && 'num', className)} {...rest} />;
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  /** Defaults to an inbox. Pass one that fits when the page has a subject. */
  icon?: ReactNode;
}) {
  return (
    <div className="px-4 py-12 text-center">
      {/* Something to rest on. An empty table was a sentence floating in the
          middle of a white rectangle, which reads more like a page that failed
          to load than one with nothing in it yet. */}
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-sunken text-lg text-ink-400 shadow-card dark:text-ink-500">
        {icon ?? <IconInbox />}
      </div>
      <p className="text-sm font-medium text-ink-800 dark:text-ink-100">{title}</p>
      {description && (
        <p className="mx-auto mt-1 max-w-md text-sm text-ink-500 dark:text-ink-400">
          {description}
        </p>
      )}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3.5 text-sm dark:border-red-500/25 dark:bg-red-500/[0.08]">
      <div className="flex items-start gap-2.5">
        <IconAlert className="mt-0.5 shrink-0 text-base text-red-600 dark:text-red-400" />
        <p className="font-medium text-red-800 dark:text-red-300">{message}</p>
      </div>
      {retry && (
        <Button variant="secondary" size="sm" className="ml-6 mt-3" onClick={retry} icon={<IconRefresh />}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-ink-500 dark:text-ink-400">
      <Spinner />
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Hold the latest onClose in a ref so it is NOT an effect dependency.
  //
  // Callers pass an inline arrow (`onClose={() => setOpen(false)}`), whose
  // identity changes on every parent render. With `onClose` in the dependency
  // array, every keystroke in a form field re-ran this effect and the
  // `panelRef.current?.focus()` below stole focus out of the input — you could
  // type exactly one character before having to click the box again.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    // Prevent the page behind from scrolling under the dialog.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the panel when the dialog OPENS, and only then.
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh] backdrop-blur-[2px] dark:bg-black/60"
      role="dialog"
      aria-modal="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cx(
          'animate-scale-in w-full rounded-xl border border-line bg-surface shadow-pop outline-none dark:bg-raised',
          wide ? 'max-w-3xl' : 'max-w-lg',
        )}
      >
        <header className="flex items-start justify-between gap-4 px-5 pb-3 pt-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink-900 dark:text-ink-50">{title}</h2>
            {description && (
              <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1.5 -mt-0.5 shrink-0 rounded-md p-1.5 text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700 dark:hover:bg-white/[0.06] dark:hover:text-ink-200"
          >
            <IconX />
          </button>
        </header>
        <div className="max-h-[60vh] overflow-y-auto px-5 pb-4 pt-1">{children}</div>
        {footer && (
          <footer className="flex justify-end gap-2 rounded-b-xl border-t border-line bg-sunken/60 px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

export function Toast({
  message,
  tone = 'good',
  onDismiss,
}: {
  message: string;
  tone?: 'good' | 'bad';
  onDismiss: () => void;
}) {
  // Same reasoning as Modal: `onDismiss` is an inline arrow at every call
  // site, so depending on it restarted the 4.5s timer on every parent render
  // and a toast on a busy page could sit there forever.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  });

  useEffect(() => {
    const timer = window.setTimeout(() => onDismissRef.current(), 4500);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div
      role="status"
      className="animate-fade-in fixed bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm rounded-lg border border-line bg-surface px-3.5 py-3 text-sm text-ink-800 shadow-pop dark:bg-raised dark:text-ink-100"
    >
      <div className="flex items-start gap-2.5">
        {/* Not colour alone. Roughly one man in twelve cannot separate this
            red from this green, and a toast is gone in four and a half
            seconds -- too fast to go looking for the wording. */}
        <span
          className={cx(
            'mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[0.7rem]',
            tone === 'good'
              ? 'bg-emerald-500 text-white'
              : 'bg-red-500 text-white',
          )}
        >
          {tone === 'good' ? <IconCheck strokeWidth={3} /> : <IconAlert strokeWidth={2.5} />}
        </span>
        <span className="flex-1 leading-5">{message}</span>
        <button
          type="button"
          onClick={onDismiss}
          className="-mr-1 shrink-0 rounded p-0.5 text-ink-400 transition-colors hover:text-ink-700 dark:hover:text-ink-200"
          aria-label="Dismiss"
        >
          <IconX />
        </button>
      </div>
    </div>
  );
}
